import {
  ProviderClaimTypesGetDTO,
  ProviderGetDTO,
} from "../../providers/provider.model";
import { BillingBreakdownChartSlice } from "./billing-service-overview.model";
import {
  BILLING_OVERVIEW_CLAIM_TYPE_ORDER,
  BillingOverviewPaymentStatus,
  BillingOverviewServiceStatus,
  BillingServiceOverview,
  BillingServiceOverviewApiResponse,
  BillingServiceOverviewPlan,
  BillingServiceOverviewSubscribedPlanResponse,
} from "./billing-service-overview.model";

export type BillingOverviewStatusTone = "success" | "warning" | "error" | "muted";

export interface BillingOverviewLegendItem {
  /** Claim-type `value` from provider claim-type cache (matches overview `claimCountByType` keys). */
  claimTypeValue: string;
  count: number;
  percentage: number;
  color: string;
}

export interface BillingOverviewLegendGroup {
  providerId: string;
  /** Translation key under `side_menu.*` when set; otherwise use `providerName`. */
  providerLabelKey: string | null;
  providerName: string;
  items: BillingOverviewLegendItem[];
}

export function formatBillingOverviewPeriodRange(
  startDate: string,
  endDate: string,
): string {
  const start = formatBillingOverviewDate(startDate);
  const end = formatBillingOverviewDate(endDate);
  return `${start} – ${end}`;
}

export function formatBillingOverviewDate(isoDate: string): string {
  const parsed = parseIsoDate(isoDate);
  if (!parsed) {
    return isoDate;
  }

  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function isBillingOverviewCancellationPending(
  overview: BillingServiceOverview | null | undefined,
): boolean {
  if (!overview) {
    return false;
  }

  return (
    overview.flags.cancelAtPeriodEnd ||
    overview.serviceStatus === "ending_soon"
  );
}

export function resolveBillingOverviewCancellationEndDateLabel(
  overview: BillingServiceOverview,
): string {
  return formatBillingOverviewDate(overview.currentPeriod.renewalDate);
}

export function formatBillingOverviewCurrency(
  amountCents: number | null | undefined,
  currency: string,
): string {
  if (amountCents == null) {
    return "---";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountCents / 100);
}

export function formatBillingOverviewPlanRange(plan: BillingServiceOverviewPlan): string {
  if (plan.maxClaims == null) {
    return `${plan.minClaims}+ cases`;
  }

  return `${plan.minClaims} - ${plan.maxClaims} cases`;
}

/** Resolves subscribed plan display fields when API returns partial/null values. */
export function resolveBillingOverviewSubscribedPlan(
  subscribedPlan: BillingServiceOverviewSubscribedPlanResponse,
  currentServicePlan: BillingServiceOverviewPlan,
): BillingServiceOverviewPlan {
  return {
    tier: subscribedPlan.tier ?? currentServicePlan.tier,
    priceId: subscribedPlan.priceId,
    amountCents: subscribedPlan.amountCents ?? currentServicePlan.amountCents,
    currency: subscribedPlan.currency || currentServicePlan.currency,
    minClaims: subscribedPlan.minClaims ?? currentServicePlan.minClaims,
    maxClaims:
      subscribedPlan.maxClaims !== undefined
        ? subscribedPlan.maxClaims
        : currentServicePlan.maxClaims,
  };
}

/** Maps raw API payload to the normalized overview used by the UI. */
export function normalizeBillingServiceOverview(
  response: BillingServiceOverviewApiResponse,
): BillingServiceOverview {
  return {
    ...response,
    subscribedPlan: resolveBillingOverviewSubscribedPlan(
      response.subscribedPlan,
      response.currentServicePlan,
    ),
  };
}

export function buildBillingOverviewChartSlices(
  overview: BillingServiceOverview,
  claimTypeValues: readonly string[],
  chartColors: Record<string, string>,
): BillingBreakdownChartSlice[] {
  const total = overview.usage.claimCount;

  return claimTypeValues.map((claimTypeValue) => {
    const count = resolveBillingOverviewClaimCount(overview, claimTypeValue);

    return {
      claimTypeValue,
      count,
      percentage: total > 0 ? (count / total) * 100 : 0,
      color:
        chartColors[claimTypeValue] ?? chartColors[BILLING_OVERVIEW_CLAIM_TYPE_ORDER[0]],
    };
  });
}

export function buildBillingOverviewLegend(
  overview: BillingServiceOverview,
  chartColors: Record<string, string>,
  orderedValues: readonly string[] = BILLING_OVERVIEW_CLAIM_TYPE_ORDER,
): BillingOverviewLegendItem[] {
  return orderedValues.map((claimTypeValue) =>
    buildBillingOverviewLegendItem(overview, claimTypeValue, chartColors),
  );
}

/** Ordered claim-type `value` list from provider claim-type cache, grouped by provider. */
export function collectBillingOverviewClaimTypeValuesFromCache(
  providerClaimTypes: ProviderClaimTypesGetDTO[],
  providers: ProviderGetDTO[],
): string[] {
  const providerOrder = resolveBillingOverviewProviderOrder(providerClaimTypes, providers);
  const seen = new Set<string>();
  const values: string[] = [];

  for (const providerId of providerOrder) {
    for (const entry of providerClaimTypes) {
      if (!entry.isActive || entry.provider !== providerId || seen.has(entry.value)) {
        continue;
      }

      seen.add(entry.value);
      values.push(entry.value);
    }
  }

  return values;
}

export function buildBillingOverviewLegendGroups(
  overview: BillingServiceOverview,
  providerClaimTypes: ProviderClaimTypesGetDTO[],
  providers: ProviderGetDTO[],
  chartColors: Record<string, string>,
): BillingOverviewLegendGroup[] {
  if (!providerClaimTypes.length) {
    return [
      {
        providerId: "all",
        providerLabelKey: null,
        providerName: "",
        items: buildBillingOverviewLegend(overview, chartColors),
      },
    ];
  }

  const providerOrder = resolveBillingOverviewProviderOrder(providerClaimTypes, providers);
  const assignedValues = new Set<string>();
  const groups: BillingOverviewLegendGroup[] = [];

  for (const providerId of providerOrder) {
    const items: BillingOverviewLegendItem[] = [];

    for (const entry of providerClaimTypes) {
      if (!entry.isActive || entry.provider !== providerId || assignedValues.has(entry.value)) {
        continue;
      }

      assignedValues.add(entry.value);
      items.push(buildBillingOverviewLegendItem(overview, entry.value, chartColors));
    }

    if (items.length) {
      groups.push({
        providerId,
        providerLabelKey: resolveBillingOverviewProviderLabelKey(providerId, providers),
        providerName: resolveBillingOverviewProviderName(providerId, providers),
        items,
      });
    }
  }

  const unassignedValues = Object.keys(
    overview.usage.claimCountByType as Record<string, number>,
  ).filter((value) => !assignedValues.has(value));

  if (unassignedValues.length) {
    groups.push({
      providerId: "other",
      providerLabelKey: "billingStatusWidget.usage.otherProviders",
      providerName: "",
      items: unassignedValues.map((claimTypeValue) =>
        buildBillingOverviewLegendItem(overview, claimTypeValue, chartColors),
      ),
    });
  }

  return groups;
}

function buildBillingOverviewLegendItem(
  overview: BillingServiceOverview,
  claimTypeValue: string,
  chartColors: Record<string, string>,
): BillingOverviewLegendItem {
  const total = overview.usage.claimCount;
  const count = resolveBillingOverviewClaimCount(overview, claimTypeValue);
  const percentage = total > 0 ? (count / total) * 100 : 0;

  return {
    claimTypeValue,
    count,
    percentage,
    color: chartColors[claimTypeValue] ?? chartColors[BILLING_OVERVIEW_CLAIM_TYPE_ORDER[0]],
  };
}

function resolveBillingOverviewClaimCount(
  overview: BillingServiceOverview,
  claimTypeValue: string,
): number {
  const counts = overview.usage.claimCountByType as Record<string, number>;
  return counts[claimTypeValue] ?? 0;
}

function resolveBillingOverviewProviderOrder(
  providerClaimTypes: ProviderClaimTypesGetDTO[],
  providers: ProviderGetDTO[],
): string[] {
  if (providers.length) {
    return providers.map((provider) => provider.value);
  }

  return [...new Set(providerClaimTypes.map((entry) => entry.provider))];
}

function resolveBillingOverviewProviderName(
  providerId: string,
  providers: ProviderGetDTO[],
): string {
  return providers.find((provider) => provider.value === providerId)?.name ?? providerId;
}

function resolveBillingOverviewProviderLabelKey(
  providerId: string,
  providers: ProviderGetDTO[],
): string | null {
  const name = resolveBillingOverviewProviderName(providerId, providers);
  return name ? `side_menu.${name}` : null;
}

export function buildBillingOverviewDonutGradient(
  overview: BillingServiceOverview,
  chartColors: Record<string, string>,
  orderedValues: readonly string[] = BILLING_OVERVIEW_CLAIM_TYPE_ORDER,
): string {
  const total = overview.usage.claimCount;
  const emptyTrack =
    typeof document !== "undefined"
      ? getComputedStyle(document.documentElement)
          .getPropertyValue("--mat-sys-outline-variant")
          .trim() || "#e5e7eb"
      : "#e5e7eb";

  if (total <= 0) {
    return `conic-gradient(${emptyTrack} 0 100%)`;
  }

  let cursor = 0;
  const segments: string[] = [];

  for (const claimTypeValue of orderedValues) {
    const count = resolveBillingOverviewClaimCount(overview, claimTypeValue);
    if (count <= 0) {
      continue;
    }

    const slice = (count / total) * 100;
    const next = cursor + slice;
    const color =
      chartColors[claimTypeValue] ?? chartColors[BILLING_OVERVIEW_CLAIM_TYPE_ORDER[0]];
    segments.push(`${color} ${cursor}% ${next}%`);
    cursor = next;
  }

  if (!segments.length) {
    return `conic-gradient(${emptyTrack} 0 100%)`;
  }

  return `conic-gradient(${segments.join(", ")})`;
}

export function resolveBillingOverviewUsageProgressPercent(
  overview: BillingServiceOverview,
): number {
  const { claimCount } = overview.usage;
  const plan = overview.currentServicePlan;

  if (claimCount <= 0) {
    return 0;
  }

  if (plan.maxClaims != null && plan.maxClaims > 0) {
    return Math.min(100, Math.round((claimCount / plan.maxClaims) * 100));
  }

  if (plan.minClaims > 0) {
    return Math.min(100, Math.round((claimCount / plan.minClaims) * 100));
  }

  return 100;
}

export type BillingOverviewProgressMarkerAnchor =
  | "start"
  | "middle"
  | "middle-end"
  | "end";

/** Keeps the tier badge inside the progress track container. */
export function resolveBillingOverviewProgressMarkerAnchor(
  progressPercent: number,
): BillingOverviewProgressMarkerAnchor {
  if (progressPercent <= 10) {
    return "start";
  }

  if (progressPercent >= 90) {
    return "end";
  }

  if (progressPercent >= 50) {
    return "middle-end";
  }

  return "middle";
}

export function resolveBillingOverviewNextTierLabel(
  plan: BillingServiceOverviewPlan,
): string {
  if (plan.maxClaims == null) {
    return `${plan.minClaims}+`;
  }

  return String(plan.maxClaims + 1);
}

export function billingOverviewServiceStatusLabelKey(
  status: BillingOverviewServiceStatus,
): string {
  return `billingStatusWidget.summary.serviceStatusValues.${status}`;
}

export function billingOverviewServiceStatusHintKey(
  status: BillingOverviewServiceStatus,
): string {
  return `billingStatusWidget.summary.serviceStatusHints.${status}`;
}

export function billingOverviewPaymentStatusLabelKey(
  status: BillingOverviewPaymentStatus,
): string {
  return `billingStatusWidget.summary.paymentStatusValues.${status}`;
}

export function billingOverviewPaymentStatusHintKey(
  status: BillingOverviewPaymentStatus,
): string {
  return `billingStatusWidget.summary.paymentStatusHints.${status}`;
}

export function resolveBillingOverviewServiceStatusTone(
  status: BillingOverviewServiceStatus,
): BillingOverviewStatusTone {
  switch (status) {
    case "active":
      return "success";
    case "ending_soon":
      return "warning";
    case "not_activated":
      return "muted";
    case "suspended":
    case "ended":
      return "error";
    default:
      return "muted";
  }
}

export function resolveBillingOverviewPaymentStatusTone(
  status: BillingOverviewPaymentStatus,
): BillingOverviewStatusTone {
  switch (status) {
    case "ok":
      return "success";
    case "none":
      return "muted";
    case "past_due":
      return "warning";
    case "unpaid":
      return "error";
    default:
      return "muted";
  }
}

export function formatBillingOverviewTimestamp(date: Date): string {
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

function parseIsoDate(value: string): Date | null {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
}
